import { AsyncJobStartResponse } from '../types/asyncJob';
import {
  Review,
  ReviewSummary,
  REVIEW_PAGE_SIZE_MAX,
  ReviewerAssignment,
  PaginatedResponse,
  CreateReviewRequest,
  AddReviewersRequest,
  ReviewStatusUpdate,
  ReviewerStatusUpdate,
  CommentCreate,
  CommentStatusUpdate,
  Reviewer,
  Comment,
  ReviewerInput,
  UserSearchResult,
  PeerReviewList,
  CustomGuidelineUploadResponse,
} from '../types/review';

import { apiClient } from './apiClient';
import { pollJob } from './jobPollingService';

/**
 * Poll attempts for the peer-review debate. The two-agent debate over the full
 * checklist can run well past the default ~10 minute window, so allow ~20
 * minutes (at the default 3s interval) before the client gives up. The backend
 * job persists its results regardless of the client poll.
 */
const PEER_REVIEW_MAX_POLL_ATTEMPTS = 400;

/**
 * Extended request timeout (ms) for a single-item Archie re-run. The debate
 * runs both debaters for the row, so allow more than the default 70s.
 */
const PEER_REVIEW_ITEM_RERUN_TIMEOUT_MS = 180000;

/**
 * Derive reviewer_emails from reviewers when not explicitly provided.
 * The backend requires reviewer_emails, so we auto-populate it
 * from the reviewers list when callers use the role-aware payload.
 */
function normalizeReviewerPayload<
  T extends {
    reviewer_emails?: string[];
    reviewers?: ReviewerInput[];
  },
>(data: T): T {
  if (data.reviewers?.length && !data.reviewer_emails?.length) {
    return {
      ...data,
      reviewer_emails: data.reviewers.map((r) => r.email),
    };
  }
  return data;
}

function clampReviewPageSize(limit: number): number {
  return Math.min(Math.max(limit, 1), REVIEW_PAGE_SIZE_MAX);
}

/**
 * Service for managing architecture reviews
 */
class ReviewService {
  /**
   * Get reviews for architectures I created (submitted for review)
   */
  async getMyReviews(
    status?: string,
    limit = 20,
    offset = 0,
  ): Promise<PaginatedResponse<ReviewSummary>> {
    const normalizedLimit = clampReviewPageSize(limit);
    const params = new URLSearchParams({
      limit: normalizedLimit.toString(),
      offset: offset.toString(),
    });
    if (status) {
      params.append('status', status);
    }
    return apiClient.get<PaginatedResponse<ReviewSummary>>(
      `/api/reviews/my-architectures?${params.toString()}`,
    );
  }

  /**
   * Get reviews where I'm assigned as a reviewer
   */
  async getAssignedReviews(
    status?: string,
    limit = 20,
    offset = 0,
  ): Promise<PaginatedResponse<ReviewerAssignment>> {
    const normalizedLimit = clampReviewPageSize(limit);
    const params = new URLSearchParams({
      limit: normalizedLimit.toString(),
      offset: offset.toString(),
    });
    if (status) {
      params.append('status', status);
    }
    return apiClient.get<PaginatedResponse<ReviewerAssignment>>(
      `/api/reviews/assigned-to-me?${params.toString()}`,
    );
  }

  /**
   * Get detailed review information by ID
   */
  async getReview(reviewId: string): Promise<Review> {
    return apiClient.get<Review>(`/api/reviews/${reviewId}`);
  }

  /**
   * Create a new review for an architecture.
   * Accepts reviewer_emails, reviewers with roles, or both.
   * When reviewers is provided, reviewer_emails is auto-derived.
   */
  async createReview(data: CreateReviewRequest): Promise<Review> {
    return apiClient.post<Review>(
      '/api/reviews',
      normalizeReviewerPayload(data),
    );
  }

  /**
   * Update review status (manual override)
   */
  async updateReviewStatus(
    reviewId: string,
    status: ReviewStatusUpdate,
  ): Promise<Review> {
    return apiClient.patch<Review>(`/api/reviews/${reviewId}/status`, status);
  }

  /**
   * Delete a review
   */
  async deleteReview(reviewId: string): Promise<void> {
    return apiClient.delete(`/api/reviews/${reviewId}`);
  }

  /**
  * Add reviewers or approvers to an existing review.
   * Accepts reviewer_emails, reviewers with roles, or both.
   * When reviewers is provided, reviewer_emails is auto-derived.
   */
  async addReviewers(
    reviewId: string,
    data: AddReviewersRequest,
  ): Promise<Reviewer[]> {
    return apiClient.post<Reviewer[]>(
      `/api/reviews/${reviewId}/reviewers`,
      normalizeReviewerPayload(data),
    );
  }

  /**
   * Remove a reviewer from a review
   */
  async removeReviewer(reviewId: string, reviewerId: string): Promise<void> {
    return apiClient.delete(
      `/api/reviews/${reviewId}/reviewers/${reviewerId}`,
    );
  }

  /**
   * Update reviewer status (approve, request changes, pending)
   */
  async updateReviewerStatus(
    reviewId: string,
    reviewerId: string,
    status: ReviewerStatusUpdate,
  ): Promise<Reviewer> {
    return apiClient.patch<Reviewer>(
      `/api/reviews/${reviewId}/reviewers/${reviewerId}/status`,
      status,
    );
  }

  /**
   * Add a comment to a review
   */
  async addComment(reviewId: string, data: CommentCreate): Promise<Comment> {
    return apiClient.post<Comment>(`/api/reviews/${reviewId}/comments`, data);
  }

  /**
   * Update comment status (open/resolved)
   */
  async updateCommentStatus(
    reviewId: string,
    commentId: string,
    status: CommentStatusUpdate,
  ): Promise<Comment> {
    return apiClient.patch<Comment>(
      `/api/reviews/${reviewId}/comments/${commentId}/status`,
      status,
    );
  }

  /**
   * Update comment text
   */
  async updateComment(
    reviewId: string,
    commentId: string,
    data: { comment: string },
  ): Promise<Comment> {
    return apiClient.patch<Comment>(
      `/api/reviews/${reviewId}/comments/${commentId}`,
      data,
    );
  }

  /**
   * Delete a comment
   */
  async deleteComment(reviewId: string, commentId: string): Promise<void> {
    return apiClient.delete(`/api/reviews/${reviewId}/comments/${commentId}`);
  }

  /**
   * Search registered users by partial email or display name.
   * Returns at most 20 matches.
   */
  async searchUsers(query: string): Promise<UserSearchResult[]> {
    const params = new URLSearchParams({ q: query });
    return apiClient.get<UserSearchResult[]>(
      `/api/sharing/search-users?${params.toString()}`,
    );
  }

  /**
   * Get the peer review checklist merged with this review's state.
   */
  async getPeerReview(reviewId: string): Promise<PeerReviewList> {
    return apiClient.get<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review`,
    );
  }

  /**
   * Set an approver's manual peer review check for a checklist item.
   * Only approvers are authorized server-side (403 otherwise).
   */
  async setPeerReviewCheck(
    reviewId: string,
    checklistItemId: number,
    checked: boolean,
  ): Promise<PeerReviewList> {
    return apiClient.patch<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/check`,
      { checked },
    );
  }

  /**
   * Save the free-text architect review for a checklist item.
   * Editable by review participants and project owners (403 otherwise).
   */
  async setArchitectReview(
    reviewId: string,
    checklistItemId: number,
    architectReview: string,
  ): Promise<PeerReviewList> {
    return apiClient.patch<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/architect-review`,
      { architect_review: architectReview },
    );
  }

  /**
   * Add an architect review comment to a checklist item's thread.
   * The author and timestamp are recorded server-side. Open to any review
   * participant (reviewer or approver) or project owner (403 otherwise).
   */
  async addArchitectComment(
    reviewId: string,
    checklistItemId: number,
    message: string,
  ): Promise<PeerReviewList> {
    return apiClient.post<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/architect-comment`,
      { message },
    );
  }

  /**
   * Add a project-specific custom "Custom Domain Checklist" item, scoped to
   * the review's project. It is assessed by the same Archie debate /
   * recommendation / fix pipeline as the enterprise items on the next run.
   * Editable by review participants and project owners (403 otherwise).
   */
  async addCustomChecklistItem(
    reviewId: string,
    description: string,
    guidelineDocuments: string[] = [],
  ): Promise<PeerReviewList> {
    return apiClient.post<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/custom-item`,
      { description, guideline_documents: guidelineDocuments },
    );
  }

  /**
   * Remove a project-specific custom checklist item. Enterprise items are
   * never affected (404 when the item is not a custom item of this project).
   */
  async deleteCustomChecklistItem(
    reviewId: string,
    checklistItemId: number,
  ): Promise<PeerReviewList> {
    return apiClient.delete<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/custom-item/${checklistItemId}`,
    );
  }

  /**
   * Upload a custom guideline document for the peer review checklist. It is
   * stored under archie-guidelines/peer-review-checklist/ and the guidelines
   * indexer is triggered so the custom checklist agent can retrieve it -
   * mirroring the knowledge base tab's upload-and-index flow.
   */
  async uploadCustomGuideline(
    reviewId: string,
    file: File,
  ): Promise<CustomGuidelineUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.postFormData<CustomGuidelineUploadResponse>(
      `/api/reviews/${reviewId}/peer-review/custom-item/guideline`,
      formData,
    );
  }

  /**
   * Run the Archie peer-review agent and return the populated checklist.
   *
   * Runs as a background job to avoid platform request timeouts: it starts
   * the job, then polls until the checklist result is ready.
   */
  async generatePeerReview(reviewId: string): Promise<PeerReviewList> {
    const job = await apiClient.post<AsyncJobStartResponse>(
      `/api/reviews/${reviewId}/peer-review/generate/async`,
    );
    return pollJob<PeerReviewList>(job.project_id, job.job_id, {
      maxAttempts: PEER_REVIEW_MAX_POLL_ATTEMPTS,
    });
  }

  /**
   * Undo a "Let Archie Fix This" run: removes Archie's additions from the
   * document, reverts the item's verdict and logs the decision as a comment.
   * Synchronous (quick document edit), so it returns the refreshed checklist.
   */
  async undoPeerReviewFix(
    reviewId: string,
    checklistItemId: number,
  ): Promise<PeerReviewList> {
    return apiClient.post<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/fix/undo`,
    );
  }

  /**
   * Run the "Let Archie Fix This" agent for one checklist item.
   *
   * Archie designs grounded improvements from the review knowledge and applies
   * them as clearly-marked additions to the generated document. Runs as a
   * background job; starts it then polls until the refreshed checklist (with
   * the fix summary on the item) is ready.
   */
  async fixPeerReview(
    reviewId: string,
    checklistItemId: number,
    userPrompt?: string,
  ): Promise<PeerReviewList> {
    const job = await apiClient.post<AsyncJobStartResponse>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/fix/async`,
      userPrompt ? { user_prompt: userPrompt } : undefined,
    );
    return pollJob<PeerReviewList>(job.project_id, job.job_id, {
      maxAttempts: PEER_REVIEW_MAX_POLL_ATTEMPTS,
    });
  }

  /**
   * Re-run the Archie peer review for a single checklist item (enterprise or
   * custom). Runs the Optimistic vs Pessimistic debate for just this row and
   * returns the refreshed checklist. Uses an extended timeout since it runs
   * both debaters.
   */
  async rerunPeerReviewItem(
    reviewId: string,
    checklistItemId: number,
  ): Promise<PeerReviewList> {
    return apiClient.postWithTimeout<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/generate`,
      undefined,
      PEER_REVIEW_ITEM_RERUN_TIMEOUT_MS,
    );
  }

  /**
   * Ask Archie a follow-up question on a checklist item. Both debaters reply
   * in character and Archie's recommendation is refreshed server-side.
   */
  async askPeerReview(
    reviewId: string,
    checklistItemId: number,
    question: string,
  ): Promise<PeerReviewList> {
    return apiClient.post<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/ask`,
      { question },
    );
  }

  /**
   * Lazily generate Archie's recommendation for a checklist item when a
   * reviewer first expands its row.
   */
  async recommendPeerReview(
    reviewId: string,
    checklistItemId: number,
  ): Promise<PeerReviewList> {
    return apiClient.post<PeerReviewList>(
      `/api/reviews/${reviewId}/peer-review/${checklistItemId}/recommend`,
    );
  }
}

export const reviewService = new ReviewService();
